import React, { useMemo } from "react";
import { Mafs, Coordinates, Plot, Polygon, Theme, Text } from "mafs";
import * as math from "mathjs";

/**
 * RiemannSum widget
 * Supports RiemannSumConfig:
 *  - function: string expression in x (e.g. "x^2", "sin(x)", "2*x+1")
 *  - domain: [min, max]
 *  - partitions: int
 *  - method: "left" | "right" | "midpoint" | "trapezoidal" (Jackson enum maps to lowercase)
 *
 * Notes:
 * - Uses config.partitions as the primary source. If variables.partitions exists and is valid, it overrides.
 * - Robust method parsing to tolerate enum values or noisy LLM output ("LEFT", "mid", "trap", etc.)
 */
export const RiemannSum = ({ config = {}, variables = {} }) => {
    const fn = useMemo(() => {
        try {
            if (!config.function || typeof config.function !== "string") return null;
            return math.compile(config.function);
        } catch (e) {
            return null;
        }
    }, [config.function]);

    const f = useMemo(() => {
        if (!fn) return () => 0;
        return (x) => {
            try {
                const y = fn.evaluate({ x });
                return typeof y === "number" && Number.isFinite(y) ? y : 0;
            } catch (e) {
                return 0;
            }
        };
    }, [fn]);

    const [min, max] = useMemo(() => {
        const d = Array.isArray(config.domain) ? config.domain : null;
        const a = d && d.length >= 2 ? Number(d[0]) : -5;
        const b = d && d.length >= 2 ? Number(d[1]) : 5;

        const safeA = Number.isFinite(a) ? a : -5;
        const safeB = Number.isFinite(b) ? b : 5;

        // Ensure increasing domain
        return safeA <= safeB ? [safeA, safeB] : [safeB, safeA];
    }, [config.domain]);

    const partitions = useMemo(() => {
        const v = variables?.partitions;
        const fromVars = Number.isFinite(Number(v)) ? Math.floor(Number(v)) : null;

        const fromConfig = Number.isFinite(Number(config.partitions))
            ? Math.floor(Number(config.partitions))
            : null;

        return Math.max(1, fromVars ?? fromConfig ?? 10);
    }, [variables, config.partitions]);

    const method = useMemo(() => {
        const raw = config.method ?? "left"; // server default is LEFT if null, but keep UI safe
        const s = String(raw).trim().toLowerCase();

        if (s.startsWith("r")) return "right";
        if (s.startsWith("mid")) return "midpoint";
        if (s.startsWith("trap")) return "trapezoidal";
        return "left";
    }, [config.method]);

    const dx = useMemo(() => {
        const span = max - min;
        // Avoid division by 0 if domain collapses
        return span === 0 ? 1 : span / partitions;
    }, [min, max, partitions]);

    const { shapes, yBound } = useMemo(() => {
        if (!fn) return { shapes: [], yBound: 5 };

        const rectsOrTraps = [];
        let maxAbsY = 0;

        for (let i = 0; i < partitions; i++) {
            const xLeft = min + i * dx;
            const xRight = xLeft + dx;

            if (method === "trapezoidal") {
                const yLeft = f(xLeft);
                const yRight = f(xRight);

                maxAbsY = Math.max(maxAbsY, Math.abs(yLeft), Math.abs(yRight));

                rectsOrTraps.push(
                    <Polygon
                        key={i}
                        points={[
                            [xLeft, 0],
                            [xLeft, yLeft],
                            [xRight, yRight],
                            [xRight, 0],
                        ]}
                        color={Theme.red}
                        opacity={0.25}
                        stroke={Theme.red}
                    />
                );
                continue;
            }

            // left / right / midpoint rectangle methods
            let xEval = xLeft;
            if (method === "right") xEval = xRight;
            if (method === "midpoint") xEval = (xLeft + xRight) / 2;

            const height = f(xEval);
            maxAbsY = Math.max(maxAbsY, Math.abs(height));

            rectsOrTraps.push(
                <Polygon
                    key={i}
                    points={[
                        [xLeft, 0],
                        [xLeft, height],
                        [xRight, height],
                        [xRight, 0],
                    ]}
                    color={Theme.red}
                    opacity={0.25}
                    stroke={Theme.red}
                />
            );
        }

        // Ensure a non-tiny bound even for near-flat functions
        return { shapes: rectsOrTraps, yBound: Math.max(2, maxAbsY) };
    }, [fn, f, partitions, min, dx, method]);

    const pad = Math.max(2, yBound * 0.2);
    const yMax = yBound + pad;
    const yMin = -yMax;

    return (
        <div style={{ position: "relative" }}>
            <Mafs viewBox={{ x: [min - 1, max + 1], y: [yMin, yMax] }} pan={false}>
                <Coordinates.Cartesian />
                {fn && <Plot.OfX y={f} color={Theme.blue} weight={2} />}
                <g>{shapes}</g>
                {!fn && (
                    <Text x={0} y={0} color="red">
                        Invalid Function
                    </Text>
                )}
            </Mafs>
        </div>
    );
};
